import { useCallback, useEffect, useRef, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import { useDebouncedValue } from "../../lib/useDebouncedValue.js";

const SEARCH_DEBOUNCE_MS = 300;
// Start the next page while a screenful is still below the fold, so scrolling
// never stops at a spinner.
const PREFETCH_PX = 160;

const INPUT_CLASS = "min-h-[32px] w-full border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]";

const EMPTY = { rows: [], cursor: null, total: null, done: false };

/**
 * Search-and-pick over a paginated endpoint. `fetchPage({ search, cursor })`
 * returns `{ rows, nextCursor, total }`; a null `nextCursor` ends the list.
 *
 * Every load is tagged with the search that issued it, so a slow page landing
 * after the next keystroke cannot append another search's rows.
 */
export default function PaginatedPicker({
    initialSearch = "",
    placeholder,
    ariaLabel,
    emptyText,
    errorText,
    fetchPage,
    renderRow,
    autoFocus = false,
    // Some feeds refuse to list: the subject candidates are search-only so the
    // endpoint cannot be used to page out the tenant roster. Say so and send
    // nothing rather than letting the server reject an empty search.
    minSearchChars = 0,
    minSearchText = "",
}) {
    const [search, setSearch] = useState(initialSearch);
    const [debounced] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
    const [page, setPage] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const activeSearch = useRef(debounced);
    const inFlight = useRef(false);
    const latestLoad = useRef(0);

    // A new search always supersedes whatever is in flight; only appends
    // deduplicate, so a slow first page cannot swallow the next keystroke.
    const load = useCallback(async (forSearch, cursor) => {
        if (cursor != null && inFlight.current) return;
        const id = latestLoad.current + 1;
        latestLoad.current = id;
        inFlight.current = true;
        activeSearch.current = forSearch;
        setLoading(true);
        setError(null);
        try {
            const result = await fetchPage({ search: forSearch, cursor });
            if (latestLoad.current !== id) return;
            setPage((current) => ({
                rows: cursor == null ? result.rows : [...current.rows, ...result.rows],
                cursor: result.nextCursor ?? null,
                total: result.total ?? null,
                done: result.nextCursor == null,
            }));
        } catch (err) {
            if (latestLoad.current === id) setError(err);
        } finally {
            if (latestLoad.current === id) {
                inFlight.current = false;
                setLoading(false);
            }
        }
    }, [fetchPage]);

    const tooShort = debounced.trim().length < minSearchChars;

    useEffect(() => {
        setPage(EMPTY);
        setError(null);
        activeSearch.current = debounced;
        if (tooShort) {
            setLoading(false);
            return;
        }
        load(debounced, null);
    }, [debounced, tooShort, load]);

    const onScroll = useCallback((event) => {
        if (page.done || loading) return;
        const el = event.currentTarget;
        if (el.scrollTop + el.clientHeight < el.scrollHeight - PREFETCH_PX) return;
        load(activeSearch.current, page.cursor);
    }, [page.done, page.cursor, loading, load]);

    return (
        <div className="grid gap-[6px]">
            <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={placeholder}
                aria-label={ariaLabel}
                autoFocus={autoFocus}
                className={INPUT_CLASS}
            />
            <div
                onScroll={onScroll}
                aria-busy={loading ? "true" : undefined}
                className="max-h-[240px] overflow-y-auto border border-[color:var(--line)]"
            >
                {page.rows.length ? (
                    <>
                        {page.rows.map((row) => renderRow(row))}
                        {loading ? (
                            <p className={`${NOTE_CLASS} m-0 border-t border-[color:var(--line-soft)] px-[9px] py-[7px] text-[10px]`}>
                                Loading more…
                            </p>
                        ) : null}
                    </>
                ) : (
                    <p className={`${NOTE_CLASS} m-0 px-[9px] py-[10px]`}>
                        {tooShort
                            ? minSearchText
                            : error ? errorText(error) : (loading ? "Searching…" : emptyText)}
                    </p>
                )}
            </div>
            {page.rows.length && error ? (
                <span className="text-[11px] leading-[1.35] text-red-400">{errorText(error)}</span>
            ) : null}
        </div>
    );
}

export function PickerRow({ primary, secondary, badge = null, selected, onSelect }) {
    return (
        <button
            type="button"
            onClick={onSelect}
            className={[
                "flex w-full items-center gap-[8px] border-b border-[color:var(--line-soft)] px-[9px] py-[6px] text-left transition-colors last:border-b-0 cursor-pointer",
                selected ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--gray-fill)]",
            ].join(" ")}
        >
            <span className="min-w-0 flex-1 truncate text-[12px] leading-[1.14] font-medium text-[color:var(--ink)]">
                {primary}
            </span>
            {secondary ? (
                <span className="min-w-0 shrink-[2] truncate text-[11px] leading-[1.14] text-[color:var(--muted)]">
                    {secondary}
                </span>
            ) : null}
            {badge}
        </button>
    );
}
